import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import ConfirmIcon from '../../svgs/ConfirmIcon';
import WarningIcon from '../../svgs/WarningIcon';
import {
  F_SIMPLE_DIALOG_ACTION_BUTTON_SX,
  F_SIMPLE_DIALOG_CONTENT_LOADING_SX,
  F_SIMPLE_DIALOG_CONTENT_SX,
  F_SIMPLE_DIALOG_PAPER_SX,
} from './styles';
import { F_SIMPLE_DIALOG_VARIANT } from './types';

import type { ReactNode } from 'react';
import type { FSimpleDialogProps } from './types';

export default function FSimpleDialog({
  title,
  titleSx,
  children,
  variant,
  actionText = 'Done',
  onAction,
  cancelText,
  onCancel,
  width,
  height,
  loading = false,
  onClose,
  ...dialogProps
}: FSimpleDialogProps) {
  let icon: ReactNode = null;
  if (variant === F_SIMPLE_DIALOG_VARIANT.SUCCESS) {
    icon = <ConfirmIcon />;
  } else if (variant === F_SIMPLE_DIALOG_VARIANT.WARNING) {
    icon = <WarningIcon />;
  }

  const { PaperProps, ...restDialogProps } = dialogProps;

  return (
    <Dialog
      {...restDialogProps}
      onClose={loading ? undefined : onClose}
      disableEscapeKeyDown={loading || restDialogProps.disableEscapeKeyDown}
      PaperProps={{
        ...PaperProps,
        sx: [
          F_SIMPLE_DIALOG_PAPER_SX,
          width == null ? {} : { width },
          height == null ? {} : { height },
          ...(Array.isArray(PaperProps?.sx) ? PaperProps.sx : [PaperProps?.sx]),
        ],
      }}
    >
      <DialogTitle component="div" sx={{ p: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {icon}
          <Typography variant="h4" fontWeight={500} sx={titleSx}>
            {title}
          </Typography>
        </Stack>
        <Divider sx={{ mt: 2 }} />
      </DialogTitle>
      <DialogContent
        sx={[F_SIMPLE_DIALOG_CONTENT_SX, loading ? F_SIMPLE_DIALOG_CONTENT_LOADING_SX : {}]}
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
            <CircularProgress />
          </Stack>
        ) : (
          children
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 0,
          justifyContent: cancelText != null && onAction != null ? 'space-between' : 'flex-end',
        }}
      >
        {cancelText == null ? null : (
          <Button
            variant="contained"
            onClick={onCancel}
            disabled={loading}
            sx={F_SIMPLE_DIALOG_ACTION_BUTTON_SX}
          >
            {cancelText}
          </Button>
        )}
        {onAction == null ? null : (
          <Button
            variant="contained"
            onClick={onAction}
            disabled={loading}
            sx={F_SIMPLE_DIALOG_ACTION_BUTTON_SX}
          >
            {actionText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
